import { useState } from "react";
import { ArrowDown, ArrowUp } from "lucide-react";

import { useT } from "../../i18n";
import type { Plume } from "../../data/types";
import { gradeForRate } from "../../data/types";
import { cx } from "../../lib/cx";
import { useFormat } from "../../lib/format";

type SortKey = "date" | "rate" | "confidence";

const GRADE_VAR: Record<string, string> = {
  low: "--plume-low",
  moderate: "--plume-moderate",
  high: "--plume-high",
  severe: "--plume-severe",
  unknown: "--plume-unknown",
};

interface Props {
  plumes: Plume[];
  /** Highlighted row (the current timeline date, YYYY-MM-DD). */
  activeDate: string | null;
  /** Row click → jump the timeline to that date and highlight the plume. */
  onPickDate: (date: string) => void;
}

const day = (iso: string) => iso.slice(0, 10);

export function DetectionsTable({ plumes, activeDate, onPickDate }: Props) {
  const t = useT();
  const fmt = useFormat();
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: "date", dir: 1 });

  const sorted = [...plumes].sort((a, b) => {
    let d = 0;
    if (sort.key === "date") d = a.sensing_time.localeCompare(b.sensing_time);
    else if (sort.key === "rate") d = (a.emission_rate_kg_hr ?? -1) - (b.emission_rate_kg_hr ?? -1);
    else d = a.max_likelihood - b.max_likelihood;
    return d * sort.dir;
  });

  const toggle = (key: SortKey) =>
    setSort((s) => (s.key === key ? { key, dir: (s.dir * -1) as 1 | -1 } : { key, dir: 1 }));

  const Th = ({ label, k, num }: { label: string; k: SortKey; num?: boolean }) => (
    <th className={cx("py-1 font-medium", num ? "text-right" : "text-left")}>
      <button
        type="button"
        onClick={() => toggle(k)}
        className={cx("inline-flex items-center gap-0.5 hover:text-text-primary", num && "flex-row-reverse")}
      >
        {label}
        {sort.key === k && (sort.dir === 1 ? <ArrowUp size={11} /> : <ArrowDown size={11} />)}
      </button>
    </th>
  );

  return (
    <table className="w-full text-xs">
      <thead className="text-text-tertiary">
        <tr className="border-b border-border-subtle">
          <Th label={t.detail.colDate} k="date" />
          <Th label={t.detail.colRate} k="rate" num />
          <Th label={t.detail.colConfidence} k="confidence" num />
          <th className="py-1 text-left font-medium">{t.detail.colSatellite}</th>
        </tr>
      </thead>
      <tbody>
        {sorted.map((p) => {
          const grade = gradeForRate(p.emission_rate_kg_hr);
          const active = day(p.sensing_time) === activeDate;
          const rateText =
            p.emission_rate_kg_hr !== null
              ? fmt.int(p.emission_rate_kg_hr)
              : p.quantification_status === "no_wind"
                ? t.detail.noWind
                : t.detail.failed;
          return (
            <tr
              key={p.plume_id}
              onClick={() => onPickDate(day(p.sensing_time))}
              className={cx(
                "cursor-pointer border-b border-border-subtle/50 transition-colors",
                active ? "bg-accent-subtle" : "hover:bg-surface-sunken",
              )}
            >
              <td className="data-value py-1">{fmt.date(day(p.sensing_time))}</td>
              <td className="data-value py-1 text-right">
                <span className="inline-flex items-center justify-end gap-1">
                  <span
                    aria-hidden
                    className="h-2 w-2 shrink-0 rounded-sm"
                    style={{ background: `var(${GRADE_VAR[grade]})` }}
                  />
                  {p.emission_rate_kg_hr === null ? (
                    <span className="text-text-tertiary">{rateText}</span>
                  ) : (
                    rateText
                  )}
                </span>
              </td>
              <td className="data-value py-1 text-right">{p.max_likelihood.toFixed(2)}</td>
              <td className="py-1">{p.sensor}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
